import { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { typographyScale } from "../../../../../../../styles/theme";
import DeleteIcon from "@mui/icons-material/Delete";
import { EditableSelectCell } from "../EditableSelectCell";
import { UNITS } from "./utils";

const specialComponents = ["Grating", "Plate", "Bolt", "Nut", "Washer"];

export const booleanOptions = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

// Conversion helpers — always store imperial, convert for display only
function toMetricWeight(val) {
  const n = Number(val);
  return isNaN(n) || !n ? "" : +(n * 0.453592).toFixed(2);
}

function toMetricArea(val) {
  const n = Number(val);
  return isNaN(n) || !n ? "" : +(n * 0.092903).toFixed(2); // ft² → m²
}

function displayWeight(val, isMetric) {
  return isMetric ? toMetricWeight(val) : val;
}

function displayArea(val, isMetric) {
  return isMetric ? toMetricArea(val) : val;
}

export const columns = (
  allTables,
  selectedSpecs,
  handleFieldUpdate,
  handleDeleteRow,
  handleSummaryRateUpdate,
  totalSummary,
  setTotalSummary,
  isMetric = false,
) => ({
  Estimation: [
    {
      accessorKey: "Spec",
      header: "Spec",
      size: 60,
      enableColumnActions: false,
      enableEditing: false,
      Cell: ({ cell }) => <span>{cell.getValue() ?? ""}</span>,
    },
    {
      accessorKey: "Components",
      header: "Component",
      size: 120,
      enableColumnActions: false,
      enableEditing: false,
      Cell: ({ cell }) => <span>{cell.getValue() ?? ""}</span>,
    },
    {
      accessorKey: "Size",
      header: "Size",
      size: 120,
      enableColumnActions: false,
      enableEditing: false,
      Cell: ({ cell }) => <span>{cell.getValue() ?? ""}</span>,
    },
    {
      accessorKey: "Material",
      header: "Material",
      size: 70,
      enableColumnActions: false,
      enableEditing: false,
      Cell: ({ cell }) => <span>{cell.getValue() ?? ""}</span>,
    },
    {
      accessorKey: "Material Description",
      header: "Material Description",
      enableColumnActions: false,
      size: 200,
      enableEditing: false,
      Cell: ({ row }) => (
        <span>{row.original["Material Description"] ?? ""}</span>
      ),
    },
    {
      accessorKey: "Qty",
      header: "Qty",
      enableColumnActions: false,
      size: 60,
      Cell: ({ row, cell }) => {
        const [localValue, setLocalValue] = useState(row.original.Qty ?? "");

        useEffect(() => {
          setLocalValue(row.original.Qty ?? "");
        }, [row.original.Qty]);

        return (
          <TextField
            type="number"
            size="small"
            variant="standard"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={() =>
              handleFieldUpdate(row.index, cell.column.id, localValue)
            }
            fullWidth
          />
        );
      },
    },
    {
      accessorKey: "length",
      header: "Dimension",
      enableColumnActions: false,
      size: 90,
      Cell: ({ row, cell }) => {
        const [localValue, setLocalValue] = useState(row.original.length ?? "");

        useEffect(() => {
          setLocalValue(row.original.length ?? "");
        }, [row.original.length]);

        return (
          <TextField
            type="text" // ← was "number", now allows "2 x 6"
            size="small"
            variant="standard"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={() =>
              handleFieldUpdate(row.index, cell.column.id, localValue)
            }
            fullWidth
          />
        );
      },
    },
    {
      accessorKey: "Unit",
      header: "Unit",
      enableColumnActions: false,
      size: 80,
      Cell: ({ cell, row }) => {
        const hasInitialized = row.original._unitInitialized;

        // Only set default unit once, when row is first created
        useEffect(() => {
          if (!hasInitialized) {
            const defaultUnit = specialComponents.includes(
              row.original.Components,
            )
              ? "Nos"
              : isMetric
                ? "m"
                : "ft";
            handleFieldUpdate(row.index, "Unit", defaultUnit);
            handleFieldUpdate(row.index, "_unitInitialized", true);
          }
        }, []); // ← empty deps: only runs once on mount

        return (
          <EditableSelectCell
            value={cell.getValue() ?? ""}
            options={UNITS}
            onChange={(newVal) =>
              handleFieldUpdate(row.index, cell.column.id, newVal)
            }
          />
        );
      },
    },
    {
      accessorKey: "UnitWeight",
      Header: (
        <div style={{ textAlign: "center" }}>
          <div>Unit Weight</div>
          <div>{isMetric ? "(kg/m)" : "(lb/ft)"}</div>
        </div>
      ),
      enableColumnActions: false,
      enableEditing: false,
      size: 90,
      // Raw value stored as lb/ft, display as kg/m when metric
      Cell: ({ row }) => (
        <span>{displayWeight(row.original.UnitWeight, isMetric)}</span>
      ),
    },
    {
      accessorKey: "totalWeight",
      Header: (
        <div style={{ textAlign: "center" }}>
          <div>Total Weight</div>
          <div>{isMetric ? "(kg)" : "(lb)"}</div>
        </div>
      ),
      enableColumnActions: false,
      enableEditing: false,
      size: 90,
      Cell: ({ row }) => (
        <span>{displayWeight(row.original.totalWeight, isMetric)}</span>
      ),
      Footer: ({ table }) => {
        let total = 0;
        table.getFilteredRowModel().rows.forEach((rr) => {
          const raw = Number(rr.original?.totalWeight || 0) || 0;
          total += isMetric ? raw * 0.453592 : raw;
        });
        return total ? (
          <Box
            sx={{
              fontSize: typographyScale.fontSize.sm,
              textAlign: "center",
              py: 0.5,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            {total.toFixed(2)}
          </Box>
        ) : (
          ""
        );
      },
    },
    {
      accessorKey: "UnitSA",
      Header: (
        <div style={{ textAlign: "center" }}>
          <div>Unit SA</div>
          <div>{isMetric ? "(m²/m)" : "(ft²/ft)"}</div>
        </div>
      ),
      enableColumnActions: false,
      enableEditing: false,
      size: 90,
      Cell: ({ row }) => (
        <span>{displayArea(row.original.UnitSA, isMetric)}</span>
      ),
    },
    {
      accessorKey: "totalSA",
      Header: (
        <div style={{ textAlign: "center" }}>
          <div>Total SA</div>
          <div>{isMetric ? "(m²)" : "(ft²)"}</div>
        </div>
      ),
      enableColumnActions: false,
      enableEditing: false,
      size: 90,
      Cell: ({ row }) => (
        <span>{displayArea(row.original.totalSA, isMetric)}</span>
      ),
      Footer: ({ table }) => {
        let total = 0;
        table.getFilteredRowModel().rows.forEach((rr) => {
          const raw = Number(rr.original?.totalSA || 0) || 0;
          total += isMetric ? raw * 0.092903 : raw;
        });
        return total ? (
          <Box
            sx={{
              fontSize: typographyScale.fontSize.sm,
              textAlign: "center",
              py: 0.5,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            {total.toFixed(2)}
          </Box>
        ) : (
          ""
        );
      },
    },
    {
      accessorKey: "FabRate",
      Header: (
        <div style={{ textAlign: "center" }}>
          <div>Fab Rate</div>
          <div>{isMetric ? "(hrs/kg)" : "(hrs/lb)"}</div>
        </div>
      ),
      enableColumnActions: false,
      enableEditing: false,
      size: 90,
      // FabRate is hrs/lb; when metric display as hrs/kg (divide by 0.453592)
      Cell: ({ row }) => {
        const raw = Number(row.original.FabRate);
        const display =
          isMetric && raw
            ? +(raw / 0.453592).toFixed(4)
            : (row.original.FabRate ?? "");
        return <span>{display}</span>;
      },
    },
    {
      accessorKey: "totalFabHrs",
      Header: (
        <div style={{ textAlign: "center" }}>
          <div>Total Fab</div>
          <div>(hours)</div>
        </div>
      ),
      enableColumnActions: false,
      enableEditing: false,
      size: 90,
      // Total fab hours are unit-agnostic (hours are hours)
      Cell: ({ row }) => <span>{row.original.totalFabHrs ?? ""}</span>,
      Footer: ({ table }) => {
        let total = 0;
        table.getFilteredRowModel().rows.forEach((rr) => {
          total += Number(rr.original?.totalFabHrs || 0) || 0;
        });
        return total ? (
          <Box
            sx={{
              fontSize: typographyScale.fontSize.sm,
              textAlign: "center",
              py: 0.5,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            {total.toFixed(2)}
          </Box>
        ) : (
          ""
        );
      },
    },
    {
      accessorKey: "Painted",
      Header: (
        <div style={{ textAlign: "center" }}>
          <div>Painted</div>
          <div>{isMetric ? "(m²)" : "(ft²)"}</div>
        </div>
      ),
      enableColumnActions: false,
      size: 90,
      muiTableHeadCellProps: { align: "center" },
      Cell: ({ row, cell }) => {
        const value = !!cell.getValue();
        const totalSA = displayArea(row.original.totalSA, isMetric);
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            onClick={() => handleFieldUpdate(row.index, cell.column.id, !value)}
            sx={{ cursor: "pointer" }}
          >
            <Checkbox checked={value} /> {value ? totalSA : ""}
          </Box>
        );
      },
    },
    {
      accessorKey: "Galvanized",
      Header: (
        <div style={{ textAlign: "center" }}>
          <div>Galvanized</div>
          <div>{isMetric ? "(m²)" : "(ft²)"}</div>
        </div>
      ),
      enableColumnActions: false,
      size: 90,
      muiTableHeadCellProps: { align: "center" },
      Cell: ({ row, cell }) => {
        const value = !!cell.getValue();
        const totalSA = displayArea(row.original.totalSA, isMetric);
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            onClick={() => handleFieldUpdate(row.index, cell.column.id, !value)}
            sx={{ cursor: "pointer" }}
          >
            <Checkbox checked={value} /> {value ? totalSA : ""}
          </Box>
        );
      },
    },
    {
      accessorKey: "FireProofed",
      Header: (
        <div style={{ textAlign: "center" }}>
          <div>Fire Proofed</div>
          <div>{isMetric ? "(m²)" : "(ft²)"}</div>
        </div>
      ),
      enableColumnActions: false,
      size: 110,
      muiTableHeadCellProps: { align: "center" },
      Cell: ({ row, cell }) => {
        const value = !!cell.getValue();
        const totalSA = displayArea(row.original.totalSA, isMetric);
        return (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            onClick={() => handleFieldUpdate(row.index, cell.column.id, !value)}
            sx={{ cursor: "pointer" }}
          >
            <Checkbox checked={value} /> {value ? totalSA : ""}
          </Box>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      enableColumnActions: false,
      size: 60,
      Cell: ({ row }) => (
        <IconButton
          color="error"
          onClick={() => handleDeleteRow(row.index)}
          aria-label="delete"
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ],
  Summary: [
    {
      accessorKey: "Material Description",
      header: "Material Description",
      enableColumnActions: false,
      size: 250,
      Footer: () => (
        <Typography sx={{ fontWeight: "bold", ml: 3, mt: 0.5 }}>
          Total
        </Typography>
      ),
    },
    {
      accessorKey: "length",
      header: "Req. Qty",
      enableColumnActions: false,
      size: 100,
    },
    {
      accessorKey: "procuredQty",
      header: "Procured Qty",
      enableColumnActions: false,
      size: 120,
      Cell: ({ row, cell }) => {
        const [localValue, setLocalValue] = useState(
          row.original.procuredQty ?? "",
        );

        useEffect(() => {
          setLocalValue(row.original.procuredQty ?? "");
        }, [row.original.procuredQty]);

        return (
          <TextField
            type="number"
            size="small"
            variant="standard"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={() =>
              handleFieldUpdate(row.index, cell.column.id, localValue)
            }
            fullWidth
          />
        );
      },
    },
    {
      accessorKey: "Unit",
      header: "Unit",
      enableColumnActions: false,
      size: 80,
    },
    {
      accessorKey: "unitCost",
      header: "Unit Cost $",
      enableColumnActions: false,
      size: 100,
    },
    {
      accessorKey: "totalCost",
      header: "Total Cost $",
      enableColumnActions: false,
      size: 120,
      Cell: ({ row }) => {
        return row.original.procuredQty &&
          row.original.unitCost !== "Not Added!"
          ? (
              Number(row.original.procuredQty) * Number(row.original.unitCost)
            ).toFixed(2)
          : "";
      },
      Footer: ({ table }) => {
        let total = 0;
        table.getFilteredRowModel().rows.forEach((rr) => {
          const r = rr.original;
          const tc =
            r.procuredQty && r.unitCost
              ? Number(r.procuredQty) * Number(r.unitCost)
              : 0;
          total += Number(tc || 0) || 0;
        });
        return total ? (
          <Box
            sx={{
              fontSize: typographyScale.fontSize.sm,
              textAlign: "center",
              py: 0.5,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            {total.toFixed(2)}
          </Box>
        ) : (
          ""
        );
      },
    },
    {
      accessorKey: "totalFabHrs",
      header: "Total Fab (hrs)",
      enableColumnActions: false,
      size: 120,
      Footer: ({ table }) => {
        let total = 0;
        table.getFilteredRowModel().rows.forEach((rr) => {
          total += Number(rr.original?.totalFabHrs || 0) || 0;
        });
        return total ? (
          <Box
            sx={{
              fontSize: typographyScale.fontSize.sm,
              textAlign: "center",
              py: 0.5,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            {total.toFixed(2)}
          </Box>
        ) : (
          ""
        );
      },
    },
  ],

  BOM: [
    {
      accessorKey: "ItemNo",
      header: "Item No",
      enableColumnActions: false,
      size: 70,
    },
    {
      accessorKey: "Material Description",
      header: "Material Description",
      enableColumnActions: false,
      size: 250,
    },
    {
      accessorKey: "Qty",
      header: "Qty",
      enableColumnActions: false,
      size: 80,
    },
    {
      accessorKey: "length",
      header: "Dimension",
      enableColumnActions: false,
      size: 100,
    },
    {
      accessorKey: "Unit",
      header: "Unit",
      enableColumnActions: false,
      size: 80,
    },
    {
      accessorKey: "Weight",
      header: isMetric ? "Weight (kg)" : "Weight (lb)",
      enableColumnActions: false,
      size: 100,
      Cell: ({ row }) => (
        <span>{displayWeight(row.original.Weight, isMetric)}</span>
      ),
    },
  ],
});

export const tabLabels = ["Estimation", "Summary", "BOM"];
